import {
  compact, filter, flattenDeep, get, groupBy,
  map, orderBy, pick, pickBy
} from 'lodash';
import {
  applicationTypes,
  applicationScriptTypes,
  scriptTypes
} from 'constants/scripts';
import {
  applicationProductSubstanceSets
} from 'constants/products';

import {
  getAvgNitrogenRateForZones,
  getTotalAreaForZones,
  getTotalProductForZones,
  getAvgProductRateForZones,
  groupZonesByProduct
} from 'util/zoneOperations';

// Selectors
import {
  getApplicationActivityCategoriesOrdered
} from 'activities/selectors';
import {
  isFieldOwner,
  getActiveFieldId,
  getActiveProductYear
} from 'current/selectors';
import {
  getSubstanceTypesForApplicationType
} from 'products/fertility-selectors';
import {
  getScriptsForFieldId,
  getScriptsSortedForManageScriptList,
  getScriptTypeForScript,
  isCompleteScript,
  isPrimaryScript,
  scriptIsInProductYear,
  getSummaryForScript,
  getApplicationTypeForScriptType
} from 'scripts/selectors';
import { getApplicationsForZoneByType } from './zones/selectors';

export const isApplicationScript = (script) => getScriptTypeForScript(script) === scriptTypes.APPLICATION;

export const getApplicationScriptsForFieldId = (state, fieldId) => {
  const fieldScripts = getScriptsForFieldId(state, fieldId);
  return pickBy(fieldScripts, (script) => isApplicationScript(script) && isCompleteScript(script));
};

export const getApplicationScriptsSortedForManageScriptList = (state, fieldScripts) => {
  // group scripts by activity type
  const fieldScriptsByActivityType = groupBy(fieldScripts, (script) => script['activity-type']);
  const applicationActivityCategoriesOrdered = getApplicationActivityCategoriesOrdered(state);
  return flattenDeep(applicationActivityCategoriesOrdered.map(({ activityType }) => {
    const activityTypeScripts = fieldScriptsByActivityType[activityType];
    return getScriptsSortedForManageScriptList(state, activityTypeScripts);
  }));
};

export const getApplicationScriptsForManageScriptList = (state) => {
  // get all scripts for the active field.
  const fieldId = getActiveFieldId(state);
  const isCurrentFieldOwner = isFieldOwner(state);
  let appScripts = getApplicationScriptsForFieldId(state, fieldId);
  // get only scripts for active product year
  appScripts = Object.values(appScripts).filter((script) => {
    if(isCurrentFieldOwner) {
      return scriptIsInProductYear(script, getActiveProductYear(state));
    } else {
      return scriptIsInProductYear(script, getActiveProductYear(state))
        && get(script, 'meta.products.length');
    }
  });

  return getApplicationScriptsSortedForManageScriptList(state, appScripts);
};
export const applicationScriptTypeIsNitrogen = (script) => {
  return get(script, 'tag') === applicationScriptTypes.NITROGEN;
};
export const getApplicationProductSubstanceSetForScript = (script) => {
  return applicationScriptTypeIsNitrogen(script)
    ? applicationProductSubstanceSets.NITROGEN
    : applicationProductSubstanceSets.LPK;
};

export const getManageApplicationSummary = (script) => {
  const { zones } = script;
  const applicationType = getApplicationTypeForScriptType(getScriptTypeForScript(script));
  const substanceSet = getApplicationProductSubstanceSetForScript(script);
  const zonesByProduct = groupZonesByProduct({ zones, applicationType });
  return orderBy(compact(map(zonesByProduct, (productZones, productName) => {
    const sampleZone = productZones[0];
    const sampleApplication = getApplicationsForZoneByType({ zone: sampleZone, applicationType })[0];
    const product = get(sampleApplication, 'product');
    const chemicalMix = get(sampleApplication, 'chemical-mix');

    const values = {
      area: getTotalAreaForZones(productZones),
      avgNitrogenRate: getAvgNitrogenRateForZones(productZones),
      avgRate: getAvgProductRateForZones(productZones, applicationType),
      product,
      chemicalMix,
      productName,
      substanceSet,
      totalProduct: getTotalProductForZones({ zones: productZones, applicationType })
    };
    return values;
  })), ['productName']);
};

export const getEditApplicationSummary = (script) => {
  const applicationSummary = getManageApplicationSummary(script)[0];
  return pick(applicationSummary, ['avgRate', 'totalProduct']);
};

export const getActivityCategoryUuidForScript = (script) => script['activity-uuid'];
export const getPrimaryApplicationScriptsForFieldId = (state, fieldId) => {
  const scripts = getApplicationScriptsForFieldId(state, fieldId);
  return filter(scripts, isPrimaryScript);
};

export const getApplicationScriptTypeBySubstanceTypeUuid = (state, substanceTypeUuid) => {
  const { byUuid } = getSubstanceTypesForApplicationType(state, applicationTypes.APPLICATION);
  const substanceTypeName = byUuid[substanceTypeUuid];
  return applicationScriptTypes[substanceTypeName.toUpperCase()];
};

export const getSubstanceTypeUuidForApplicationScriptType = (state, applicationScriptType) => {
  const substanceTypeName = applicationScriptType.substr(applicationScriptType.lastIndexOf('-') + 1);
  const { byName } = getSubstanceTypesForApplicationType(state, applicationTypes.APPLICATION);
  return byName[substanceTypeName];
};

export const getIncorporationMethodForScript = (script) => {
  const summary = getSummaryForScript(script);
  return get(summary, '[0].incorporation-method');
};
